/** @format */

import type { QueryKey, UseQueryOptions, UseQueryResult } from "@tanstack/react-query";
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";

import type { PlaybackState } from "@/context/playbackContext";
import { scopeFromPlaybackState } from "./playbackScope";

interface PlaybackQueryConfig<TData, TQueryFnData = TData> {
  runID?: string | null;
  resource: string | string[];
  playback: PlaybackState;
  fetchFn: (ctx: { runID: string; playback: PlaybackState }) => Promise<TData>;
  options?: Omit<UseQueryOptions<TData, Error, TQueryFnData>, "queryKey" | "queryFn">;
}

export function usePlaybackQuery<TData, TQueryFnData = TData>({
  runID,
  resource,
  playback,
  fetchFn,
  options,
}: PlaybackQueryConfig<TData, TQueryFnData>): UseQueryResult<TQueryFnData, Error> {
  const scope = useMemo(() => scopeFromPlaybackState(playback), [playback]);

  const keySegments = useMemo(() => {
    return Array.isArray(resource) ? resource : [resource];
  }, [resource]);

  const queryKey = useMemo<QueryKey>(
    () => ["run", runID ?? "", ...keySegments, scope],
    [runID, keySegments, scope]
  );

  const enabled = (options?.enabled ?? true) && !!runID && runID !== "undefined";

  return useQuery<TData, Error, TQueryFnData>({
    ...options,
    queryKey,
    enabled,
    queryFn: async () => {
      if (!runID) {
        throw new Error("runID is required for playback queries");
      }
      return await fetchFn({ runID, playback });
    },
  });
}
